import React, { useState } from 'react';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { t } from 'ttag';

import Modal from '@ui/Modal';
import MarkSoldModal, { Thread } from '@components/MarkSoldModal';
import MarkSoldSuccessModal from '@components/MarkSoldSuccessModal';
import ProductModalBackground from '@components/ProductModalBackground';
import { ConfirmSaleErrorModal } from '@components/ConfirmSaleErrorModal';
import {
  MarksSoldUpdateDataFragmentDoc,
  MarksSoldUpdateDataFragment,
} from '@models';
import { containSaleConfirmationRequiredError } from '@utils/common';
import { ConfirmSaleBigDiscountModal } from '@components/ConfirmSaleBigDiscountModal';
import { editProduct } from '@urls';
import { logger } from '@utils/logger';

import {
  useConfirmMarkProductAsSoldModalMutation,
  useMarkProductAsSoldModalProductLazyQuery,
} from './gql/graphql.v3.generated';

import s from './MarkSoldButton.sss';

enum Modals {
  SUCCESS,
  FAIL,
  MAIN,
  CONFIRM_SALE,
  BIG_DISCOUNT,
}

export type MarkSoldButtonProps = {
  children: (args: { openModal: () => void }) => React.ReactNode;
  productId: number;
  onCloseSuccessModal?: () => void;
};

export const MarkSoldButton: React.FC<MarkSoldButtonProps> = ({
  children,
  productId,
  onCloseSuccessModal,
}) => {
  const location = useLocation();
  const history = useHistory();
  const [openedModal, setOpenedModal] = useState<Modals>();
  const [mutationErrors, setMutationErrors] = useState<GraphFieldErrorList[]>();
  const [confirmationData, setConfirmationData] = useState<Thread | null>();
  const [selectedThread, setSelectedThread] = useState<Thread | null>(null);

  const [
    fetchProductDetail,
    { data: productDetailsData, called },
  ] = useMarkProductAsSoldModalProductLazyQuery({
    variables: { productId },
    onCompleted: ({ productDetails }) => {
      if (productDetails.threads.length) {
        setSelectedThread(productDetails.threads[0]);
      }
    },
  });

  const [markSold, { loading: isSubmitting }] = useConfirmMarkProductAsSoldModalMutation({
    update: (cache, result) => {
      const updatedProduct = result.data?.markProductAsSold?.product;
      if (!updatedProduct) {
        return;
      }

      const data: MarksSoldUpdateDataFragment = {
        statusTitle: updatedProduct.statusTitle,
        productCount: updatedProduct.productCount,
      };

      cache.writeFragment({
        data,
        id: `Product:${updatedProduct.id}`,
        fragment: MarksSoldUpdateDataFragmentDoc,
      });
    },
  });

  const onClose = () => setOpenedModal(undefined);

  const onClickOnChildren = () => {
    fetchProductDetail();
    setOpenedModal(Modals.MAIN);
  };

  const onFail = (errors: GraphFieldErrorList[]) => {
    setMutationErrors(errors);
    setOpenedModal(Modals.FAIL);
  };

  const handleSubmit = async (thread: Thread) => {
    const res = await markSold({
      variables: {
        productId: productId,
        buyerId: thread.participant.id,
        confirmSale: !!confirmationData,
      },
    });
    const errors = res?.data?.markProductAsSold?.errors;

    if (errors?.length) {
      setMutationErrors(errors);
      setConfirmationData(thread);

      if (containSaleConfirmationRequiredError(errors)) {
        setOpenedModal(Modals.CONFIRM_SALE);
      } else {
        onFail(errors);
      }
    } else {
      setOpenedModal(Modals.SUCCESS);
    }
  };

  const handleMarkSold = async () => {
    if (selectedThread) {
      if (selectedThread.hasBigDiscount) {
        setConfirmationData(selectedThread);
        setOpenedModal(Modals.BIG_DISCOUNT);
      } else {
        await handleSubmit(selectedThread);
      }
    }
  };

  const handleConfirmSale = async () => {
    if (confirmationData) {
      await handleSubmit(confirmationData);
      setConfirmationData(null);
    }
  };

  const handleEditProduct = () => {
    const editProductPathname = generatePath(editProduct, { productId });

    const editProductUrl = history.createHref({
      pathname: editProductPathname,
      search: queryString.stringify({
        next: location.pathname,
      }),
    });

    window.location.href = editProductUrl;
  };

  if (!productDetailsData) {
    return (
      <>{children({ openModal: onClickOnChildren })}</>
    );
  }

  if (called && !productDetailsData) {
    logger.error('Empty render');
    return null;
  }

  const product = productDetailsData.productDetails;
  const { threads } = product;
  return (
    <>
      {children({ openModal: onClickOnChildren })}
      {threads?.length ? (
        <Modal
          isOpen={openedModal === Modals.MAIN}
          onRequestClose={onClose}
          contentLabel="Modal"
          className={s.modal}
        >
          <MarkSoldModal
            threads={threads}
            selectedThread={selectedThread}
            product={{
              id: productId,
              img: product.photos[0].thumbnailUrl,
              name: product.name,
            }}
            isSubmitting={isSubmitting}
            onCancel={onClose}
            onSubmit={handleMarkSold}
            onSelect={(thread) => setSelectedThread(thread)}
          />
        </Modal>
      ) : (
        <Modal
          isOpen={openedModal === Modals.MAIN}
          onRequestClose={onClose}
          withClose={true}
        >
          <ProductModalBackground
            productImg={product.photos[0].thumbnailUrl}
            productName={product.name}
          />
          <div className={s.modalText}>
            {t`Щоб відмітити продаж цього товару, перейдіть на деталі відповідного замовлення та позначте замовлення виконаним.`}
          </div>
        </Modal>
      )}
      <MarkSoldSuccessModal
        isOpen={openedModal === Modals.SUCCESS}
        onRequestClose={() => {
          if (onCloseSuccessModal) {
            onCloseSuccessModal();
          }
          onClose();
        }}
      />

      <ConfirmSaleBigDiscountModal
        isOpen={openedModal === Modals.BIG_DISCOUNT}
        isSubmitting={isSubmitting}
        onClose={() => setOpenedModal(Modals.MAIN)}
        onEditProduct={handleEditProduct}
        onConfirmSale={handleConfirmSale}
      />

      {!!confirmationData && (
        <ConfirmSaleErrorModal
          isOpen={(openedModal === Modals.FAIL) || (openedModal === Modals.CONFIRM_SALE)}
          error={mutationErrors?.[0]}
          productId={productId}
          buyerUsername={confirmationData.participant.username}
          commissionPercent={confirmationData.commissionPercent}
          commission={confirmationData.totalCommission}
          threadId={confirmationData.threadId}
          onClose={onClose}
          onApprove={() => confirmationData && handleSubmit(confirmationData)}
        />
      )}
    </>
  );
};

export default MarkSoldButton;
